import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { Notifications } from '../services/Hub/notificationCenter/notificationCenterApi'
import {
  NotificationSetupTemplate,
  NotificationMessageTemplate,
  OrganizationNotificationTrigger,
  NotificationEngineSourceDTO
} from '../services/Hub/notificationCenter/types'

interface NotificationCenterState {
  triggers: OrganizationNotificationTrigger[]
  triggersPagination: {
    total: number
    currentPage: number
    itemsPerPage: number
  }
  setupTemplates: NotificationSetupTemplate[]
  messageTemplates: NotificationMessageTemplate[]
  engineSourceFields: NotificationEngineSourceDTO[]
  triggerSources: any
  isLoading: boolean
  error: string | null
  selectedTriggerDetails: OrganizationNotificationTrigger | null
  isLoadingTriggerDetails: boolean
}

const initialState: NotificationCenterState = {
  triggers: [],
  triggersPagination: {
    total: 0,
    currentPage: 1,
    itemsPerPage: 10
  },
  setupTemplates: [],
  messageTemplates: [],
  engineSourceFields: [],
  triggerSources: [],
  isLoading: false,
  error: null,
  selectedTriggerDetails: null,
  isLoadingTriggerDetails: false
}

export const fetchSetupTemplates = createAsyncThunk(
  'notificationCenter/fetchSetupTemplates',
  async (params: any) => {
    return await Notifications.setupTemplates.fetch(params)
  }
)

export const fetchMessageTemplates = createAsyncThunk(
  'notificationCenter/fetchMessageTemplates',
  async (params: any) => {
    return await Notifications.templates.fetch(params)
  }
)

export const fetchTriggers = createAsyncThunk(
  'notificationCenter/fetchTriggers',
  async (params: any) => {
    return await Notifications.triggers.fetch(params)
  }
)

export const fetchEngineSourceFields = createAsyncThunk(
  'notificationCenter/fetchEngineSourceFields',
  async (params: { triggerSource: string; organizationId?: string }) => {
    return await Notifications.engineSources.fetchFields(params.triggerSource, params.organizationId)
  }
)

export const fetchTriggerSources = createAsyncThunk(
  'notificationCenter/fetchTriggerSources',
  async () => {
    return await Notifications.triggers.sources()
  }
)

export const createSetupTemplate = createAsyncThunk(
  'notificationCenter/createSetupTemplate',
  async (data: Partial<NotificationSetupTemplate>) => {
    return await Notifications.setupTemplates.create(data);
  }
);

export const updateSetupTemplate = createAsyncThunk(
  'notificationCenter/updateSetupTemplate',
  async (data: NotificationSetupTemplate) => {
    return await Notifications.setupTemplates.update(data.id, data)
  }
)

export const deleteSetupTemplate = createAsyncThunk(
  'notificationCenter/deleteSetupTemplate',
  async (id: string) => {
    await Notifications.setupTemplates.delete(id)
    return id
  }
)

export const fetchSetupTemplateDetails = createAsyncThunk(
  'notificationCenter/fetchSetupTemplateDetails',
  async (id: string) => {
    const result = await Notifications.setupTemplates.fetchDetails(id);
    return result;
  }
);

// New actions for organization notifications
export const fetchTriggersByOrganization = createAsyncThunk(
  'notificationCenter/fetchTriggersByOrganization',
  async (params: { organizationId: string; page?: number; itemsPerPage?: number; name?: string }) => {
    return await Notifications.triggers.fetch(params)
  }
)

export const deleteTrigger = createAsyncThunk(
  'notificationCenter/deleteTrigger',
  async (triggerId: string) => {
    await Notifications.triggers.delete(triggerId)
    return triggerId
  }
)

export const updateTrigger = createAsyncThunk(
  'notificationCenter/updateTrigger',
  async (data: { id: string; triggerData: Partial<OrganizationNotificationTrigger> }) => {
    return await Notifications.triggers.update(data.id, data.triggerData)
  }
)

export const fetchTriggerDetails = createAsyncThunk(
  'notificationCenter/fetchTriggerDetails',
  async (triggerId: string) => {
    return await Notifications.triggers.fetchDetails(triggerId)
  }
)

export const notificationCenterSlice = createSlice({
  name: 'notificationCenter',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSetupTemplates.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchSetupTemplates.fulfilled, (state, action) => {
        state.isLoading = false
        state.setupTemplates = action.payload.items
      })
      .addCase(fetchSetupTemplates.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? null
      })
      .addCase(fetchMessageTemplates.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchMessageTemplates.fulfilled, (state, action) => {
        state.isLoading = false
        state.messageTemplates = action.payload.items
      })
      .addCase(fetchMessageTemplates.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? null
      })
      .addCase(fetchTriggers.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchTriggers.fulfilled, (state, action) => {
        state.isLoading = false
        state.triggers = action.payload.items
      })
      .addCase(fetchTriggers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? null
      })
      .addCase(fetchTriggersByOrganization.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchTriggersByOrganization.fulfilled, (state, action) => {
        state.isLoading = false
        state.triggers = action.payload.items
        state.triggersPagination = {
          total: action.payload.total,
          currentPage: action.payload.currentPage,
          itemsPerPage: action.payload.itemsPerPage
        }
      })
      .addCase(fetchTriggersByOrganization.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? null
      })
      .addCase(deleteTrigger.pending, state => {
        state.isLoading = true
      })
      .addCase(deleteTrigger.fulfilled, (state, action) => {
        state.isLoading = false
        state.triggers = state.triggers.filter(trigger => trigger.id !== action.payload)
      })
      .addCase(deleteTrigger.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? null
      })
      .addCase(updateTrigger.pending, state => {
        state.isLoading = true
      })
      .addCase(updateTrigger.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.triggers.findIndex(trigger => trigger.id === action.payload.id)
        if (index !== -1) {
          state.triggers[index] = action.payload
        }
      })
      .addCase(updateTrigger.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? null
      })
      .addCase(fetchTriggerDetails.pending, state => {
        state.isLoadingTriggerDetails = true
      })
      .addCase(fetchTriggerDetails.fulfilled, (state, action) => {
        state.isLoadingTriggerDetails = false
        state.selectedTriggerDetails = action.payload
      })
      .addCase(fetchTriggerDetails.rejected, (state, action) => {
        state.isLoadingTriggerDetails = false
        state.error = action.error.message ?? null
      })
      .addCase(fetchEngineSourceFields.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchEngineSourceFields.fulfilled, (state, action) => {
        state.isLoading = false
        state.engineSourceFields = action.payload
      })
      .addCase(fetchEngineSourceFields.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? null
      })
      .addCase(fetchTriggerSources.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchTriggerSources.fulfilled, (state, action) => {
        state.isLoading = false
        state.triggerSources = action.payload
      })
      .addCase(fetchTriggerSources.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? null
      })
      .addCase(createSetupTemplate.fulfilled, (state, action) => {
        state.setupTemplates.push(action.payload);
      })
      .addCase(updateSetupTemplate.pending, state => {
        state.isLoading = true
      })
      .addCase(updateSetupTemplate.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.setupTemplates.findIndex(template => template.id === action.payload.id)
        if (index !== -1) {
          state.setupTemplates[index] = action.payload
        }
      })
      .addCase(deleteSetupTemplate.pending, state => {
        state.isLoading = true
      })
      .addCase(deleteSetupTemplate.fulfilled, (state, action) => {
        state.isLoading = false
        state.setupTemplates = state.setupTemplates.filter(
          template => template.id !== action.payload
        )
      })
      .addCase(fetchSetupTemplateDetails.fulfilled, (state, action) => {
        const index = state.setupTemplates.findIndex(template => template.id === action.payload.id);
        if (index !== -1) {
          state.setupTemplates[index] = action.payload;
        }
      })
  }
})

export default notificationCenterSlice.reducer
